import { FC, useContext, useEffect, useRef, useState } from "react";
import NotificationDispatch, { showErrorNotification } from "../../../../../context/notificationContext";
import { useSearchContext } from "../../../../../context/searchContext";
import { useDebounce } from "../../../../../hooks/useDebounce";
import { useEntityAccesses } from "../../../../../hooks/useEntityAccesses";
import { useFilterSearch } from "../../../../../hooks/useFilterSearch";
import useListFilters from "../../../../../hooks/useListFilters";
import useI18n from "../../../../../hooks/useTranslations";
import { HrailItemExtra } from "../../../../../http/hrail/types/itemExtras";
import { useHrailItemExtrasApi } from "../../../../../http/hrail/useItemExtras";
import { PaginationQuery } from "../../../../../types";
import FlexContent from "../../../../ui/FlexContent";
import { getDeleteAction, getEditAction } from "../../../../ui/ListItemMenu";
import ModalDialog from "../../../../ui/ModalDialog";
import SearchBar from "../../../../ui/SearchBar";
import Table from "../../../../ui/Table";
import { CreateItemExtraModalDialogButton } from "./CreateItemExtraModalDialogButton";
import { ItemExtraForm } from "./ItemExtraForm";

type SelectOption = {
  id: string;
  label: string;
};

type ItemExtrasProps = {
  active?: boolean;
  fixedItem?: SelectOption;
  fixedExtraItem?: SelectOption;
};

export const ItemExtras: FC<ItemExtrasProps> = ({ active, fixedItem, fixedExtraItem }) => {
  const i18n = useI18n();
  const dispatch = useContext(NotificationDispatch);
  const filters = useListFilters({ key: "itemExtras" });
  const aborter = useRef(new AbortController());
  const itemExtraApi = useHrailItemExtrasApi();
  const [itemToEdit, setItemToEdit] = useState<HrailItemExtra>();
  const [itemToDelete, setItemToDelete] = useState<HrailItemExtra>();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { setActions, setTitle } = useSearchContext();
  const { searchTerm, handleSearchChange } = useFilterSearch(filters, aborter, "itemExtras");
  const isActiveTab = active ?? true;

  const { accessesAction, accessesDialog } = useEntityAccesses<HrailItemExtra>("hrail.item_extra");

  const debouncedValue = useDebounce(searchTerm);

  const query: PaginationQuery = {
    ...filters.state,
    q: debouncedValue,
  };

  const { error, data } = itemExtraApi.useAdminHrailItemExtras(query, aborter.current.signal);

  useEffect(() => {
    if (!isActiveTab) return;

    setTitle(i18n.translation.itemExtras.plural);
    setActions(
      <>
        <SearchBar
          placeholder={i18n.translation.itemExtras.search}
          value={searchTerm}
          onChange={handleSearchChange}
        />
        <CreateItemExtraModalDialogButton fixedItem={fixedItem} fixedExtraItem={fixedExtraItem} />
      </>,
    );

    return () => {
      setTitle(undefined);
      setActions(null);
    };
  }, [isActiveTab, searchTerm]);

  return (
    <FlexContent padding>
      <Table<HrailItemExtra>
        isTall={true}
        filters={filters}
        head={
          <tr>
            <th align="left">{i18n.translation.items.singular}</th>
            <th align="left">{i18n.translation.common.extraItem}</th>
            <th align="right">{i18n.translation.common.quantityPerItem}</th>
            <th></th>
          </tr>
        }
        items={data?.items}
        renderRow={(itemExtra) => (
          <>
            <Table.Td noWrap>{itemExtra.item.code}</Table.Td>
            <Table.Td noWrap>{itemExtra.extraItem.code}</Table.Td>
            <Table.Td align="right">{itemExtra.quantityPerItem}</Table.Td>
          </>
        )}
        onRowClick={(itemExtra) => setItemToEdit(itemExtra)}
        rowActions={(itemExtra) => [
          getEditAction(i18n, () => setItemToEdit(itemExtra)),
          accessesAction(itemExtra),
          getDeleteAction(i18n, () => setItemToDelete(itemExtra), true),
        ]}
        total={data?.total}
        error={!!error}
      />
      {accessesDialog}

      {!!itemToEdit && (
        <ModalDialog onClose={() => setItemToEdit(undefined)} title={i18n.translation.itemExtras.edit}>
          <ItemExtraForm
            itemExtra={itemToEdit}
            fixedItem={fixedItem}
            fixedExtraItem={fixedExtraItem}
            onClose={() => setItemToEdit(undefined)}
          />
        </ModalDialog>
      )}

      {!!itemToDelete && (
        <ModalDialog
          onClose={() => setItemToDelete(undefined)}
          title={i18n.translation.itemExtras.delete}
          isSubmitting={isSubmitting}
          actions={[
            {
              title: i18n.translation.common.cancel,
              onClick: () => setItemToDelete(undefined),
              type: "secondary",
            },
            {
              title: i18n.translation.common.yes,
              onClick: () => {
                setIsSubmitting(true);
                itemExtraApi
                  .deleteAdminHrailItemExtra(itemToDelete.id)
                  .then(() => setItemToDelete(undefined))
                  .catch((err) => dispatch(showErrorNotification(i18n, err)))
                  .finally(() => setIsSubmitting(false));
              },
            },
          ]}
        >
          {i18n.translation.common.deleteEntityQuestion(
            i18n.translation.itemExtras.deleteLabel,
            getItemExtraLabel(itemToDelete),
          )}
        </ModalDialog>
      )}
    </FlexContent>
  );
};

const getItemExtraLabel = (itemExtra: HrailItemExtra) =>
  [itemExtra.item.code, itemExtra.extraItem.code].filter(Boolean).join(" - ") || itemExtra.id;
